/**
 * @fileoverview Middleware Redux para sincronização automática do carrinho.
 * Intercepta ações do carrinho e sincroniza com servidor quando usuário autenticado.
 * 
 * @module cartSyncMiddleware
 * @since 1.0.0
 */

import { saveCartAsync, saveCartActionAsync } from '../slices/cartSlice';

/**
 * Middleware para sincronização automática do carrinho com operações atômicas.
 * Intercepta ações de modificação do carrinho e dispara sincronização específica.
 * 
 * @param {Object} store - Store Redux
 * @returns {Function} Middleware function
 */
export const cartSyncMiddleware = (store) => (next) => (action) => {
  const prevState = store.getState();
  const result = next(action);
  const currentState = store.getState();
  
  // Ações que devem disparar sincronização atômica
  const atomicSyncActions = {
    'cart/addToCart': 'insert',
    'cart/updateCartQuantity': 'update', 
    'cart/removeFromCart': 'delete'
  };
  
  // Ações que usam sincronização legada
  const legacySyncActions = ['cart/clearCartWithSync'];
  
  const { isAuthenticated, user, isLoggingOut } = currentState.auth;
  
  // Não sincronizar durante logout ou se não autenticado
  if (!isAuthenticated || !user?.email || isLoggingOut) {
    return result;
  }
  
  if (atomicSyncActions[action.type]) {
    // Nova lógica: operações atômicas
    const actionType = atomicSyncActions[action.type];
    let productData;
    
    if (actionType === 'insert') {
      // Produto adicionado
      const product = action.payload;
      productData = {
        productId: String(product.id),
        quantity: Number(product.quantity) || 1,
        price: Number(product.preco) || 0,
        name: String(product.nome || ''),
        brand: String(product.marca || ''),
        image: String(product.imagem || ''),
        category: String(product.categoria || '')
      };
    } else if (actionType === 'update') {
      // Quantidade atualizada
      const { id, quantity } = action.payload;
      productData = {
        productId: String(id),
        quantity: Number(quantity)
      };
    } else if (actionType === 'delete') {
      // Produto removido
      const productId = action.payload;
      productData = {
        productId: String(productId)
      };
    }
    
    // Debounce para evitar muitas requisições
    clearTimeout(window.cartSyncTimeout);
    window.cartSyncTimeout = setTimeout(() => {
      // Verificar novamente se ainda está autenticado
      const latestState = store.getState();
      if (!latestState.auth.isAuthenticated) return;
      
      store.dispatch(saveCartActionAsync({ action: actionType, cartData: [productData] }));
    }, 500);
    
  } else if (legacySyncActions.includes(action.type)) {
    // Lógica legada: substituição completa
    const { items } = currentState.cart;
    
    clearTimeout(window.cartSyncTimeout);
    window.cartSyncTimeout = setTimeout(() => {
      const latestState = store.getState();
      if (!latestState.auth.isAuthenticated) return;
      
      // map: converte itens para formato da API com tipos corretos e validação
      const cartData = items
        .filter(item => item.id && item.preco != null && item.quantity > 0)
        .map(item => ({
          productId: String(item.id),
          quantity: Number(item.quantity) || 1,
          price: Number(item.preco) || 0,
          name: String(item.nome || ''),
          brand: String(item.marca || ''),
          image: String(item.imagem || ''),
          category: String(item.categoria || '')
        }));
      
      store.dispatch(saveCartAsync(cartData));
    }, 1000);
  }
  
  return result;
};

export default cartSyncMiddleware;